/**
 * Panel Header Component
 *
 * Terminal-style title bar with crown icon and control buttons.
 */

import { cn } from '@pray/shared/utils/styles';

import type { PanelHeaderProps, PanelPosition } from '../types';

import styles from './PanelHeader.module.scss';

// Crown icon for Solomon AI branding
function CrownIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2 17l2-11 5 5 3-7 3 7 5-5 2 11H2z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Minimize icon
function MinimizeIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

// Close icon
function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18 6L6 18M6 6L18 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Panel to bottom icon (horizontal bar at bottom)
function PanelBottomIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M3 15H21" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

// Panel to right icon (vertical bar at right)
function PanelRightIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M15 3V21" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

// Position toggle component - switches between bottom and right
function PositionToggle({
  position,
  onPositionChange,
}: {
  position: PanelPosition;
  onPositionChange?: (position: PanelPosition) => void;
}) {
  if (!onPositionChange) return null;

  const nextPosition = position === 'bottom' ? 'right' : 'bottom';
  const Icon = position === 'bottom' ? PanelRightIcon : PanelBottomIcon;
  const label = position === 'bottom' ? 'Move panel to right side' : 'Move panel to bottom';

  return (
    <button
      type="button"
      className={styles.controlButton}
      onClick={() => onPositionChange(nextPosition)}
      aria-label={label}
      title={label}
    >
      <Icon />
    </button>
  );
}

export function PanelHeader({
  title = 'Solomon',
  onMinimize,
  onClose,
  position = 'bottom',
  onPositionChange,
  className,
}: PanelHeaderProps) {
  // Header is always static - just shows "Solomon" with crown icon

  return (
    <header className={cn(styles.header, className)}>
      <div className={styles.titleSection}>
        <CrownIcon className={styles.crownIcon} />
        <h2 className={styles.title}>{title}</h2>
      </div>

      <div className={styles.controls}>
        <PositionToggle position={position} onPositionChange={onPositionChange} />

        {onMinimize && (
          <button
            type="button"
            className={styles.controlButton}
            onClick={onMinimize}
            aria-label="Minimize panel"
            title="Minimize"
          >
            <MinimizeIcon />
          </button>
        )}

        {onClose && (
          <button
            type="button"
            className={styles.controlButton}
            onClick={onClose}
            aria-label="Close panel"
            title="Close"
          >
            <CloseIcon />
          </button>
        )}
      </div>
    </header>
  );
}

export default PanelHeader;
